import React from "react";
import SaveWithMetaData from "../../src/taskpane/components/directoryComponents/sharepoint/SaveToSharepoint";
import { useDispatch, useSelector } from "react-redux";
import { SharePointInputData } from "../../src/taskpane/interfaces/props/SharePointInputData";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { Dropdown, Icon, IPanelStyles, IStackItemStyles, IStackStyles, IStackTokens, ITooltipHostStyles, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, ResponsiveMode, Separator, Spinner, SpinnerSize, Stack, Toggle, TooltipHost, TooltipOverflowMode } from "@fluentui/react";
import { Slide, ToastContainer } from "react-toastify";
import { DatasourceValidationHelper } from "../../src/taskpane/components/helpers/datasourceValidationHelper";
import { setDatasourceValues } from "../../src/taskpane/redux/modules/datasource";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { IFileToUpload } from "../../src/taskpane/interfaces/IFileToUpload";
import generalModule from "../../utilities/GeneralModule";
import { FolderView } from "../../src/taskpane/components/helpers/folderView";
import { logger } from "../../utilities/logger";
import { FileOption } from "../../src/taskpane/interfaces/IFileOption";
import { getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import { VersionSelectorMinMajOnly } from "../../src/taskpane/components/_v2/_shared/VersionSelector/VersionSelectorMinMajOnly";
import { CheckinTypes } from "../../src/taskpane/components/_v2/redux/api/local/api-local-interfaces";
import DuplicateFiles from "../../src/taskpane/components/directoryComponents/oneDrive/DuplicateFiles";
import { DragAndDropHelper } from "../../utilities/DragAndDropHelper";
import { sharePointInternalFileName, sharePointInternalTitleName } from "../../src/taskpane/config/constants";
import { getSelectedExchangeItemsAsync, resolveEmailMappedInputValue } from "../../src/taskpane/components/helpers/emailMappedFileValues";
import { IFilePreCommitResult } from "../../src/taskpane/interfaces/IFilePreCommitResult";
import authModule from "../../utilities/AuthModule";
import { getFilePreCommitInfo } from "../../src/taskpane/services/SharePointRestServices";
import { setFile } from "../../src/taskpane/redux/modules/file";
import { SaveMode } from "../../src/taskpane/components/enums/SaveModeEnum";
import { ISaveButtonProps } from "../../src/taskpane/interfaces/ISaveButtonProps";
import { getPlatform } from "../../src/taskpane/styles/mobileStyles";
import { refrehOptionsRequest } from "../../src/taskpane/config/datasourceConstants";
import { DatasourceHelper } from "../../src/taskpane/components/helpers/dataSourceHelper";
import { resolveDestinationUrl } from "../../src/taskpane/components/helpers/urlDestination";

const containerStackTokens: IStackTokens = {
  childrenGap: 5
};

const containerStackStyles: IStackStyles = {
  root: {
    height: "100%",
  }
};

const metadataStackItemStyles: IStackItemStyles = {
  root: {
    overflowY: "auto"
  }
};

const tooltipHostCalloutProps = { gapSpace: 7 };

const stackTokens: IStackTokens = {
  childrenGap: 8
};

const fileOptionStackStyles: IStackStyles = {
  root: {
    minWidth: 0
  }
}

const fileTypeIconStyles: React.CSSProperties = {
  flexShrink: 0
}

const fileNameStyles: ITooltipHostStyles = {
  root: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    msTextOverflow: "ellipsis"
  }
}

const dropdownStyles: React.CSSProperties = {
  marginTop: "10px",
  paddingLeft: "10px",
  paddingRight: "10px",
  marginBottom: "-10px"
};

export interface ICMMultipleFilesMetaDataPanelProps {
  closePanel?: () => void;
  panelIsOpen: boolean;
  originalInputValues: SharePointInputData[];
  saveFiles?: (savemode: SaveMode, selectedFile?: FileOption, optionsSelected?: boolean, autoRenameDuplicateFolder?: boolean) => void;
  fileOptions: FileOption[];
  setFileOptions?: (fileOptions: any[]) => void;
  checkForDuplicates: boolean;
  setCheckForDuplicates?: (checkForDuplicates: boolean) => void;
  showSelectVersion?: boolean;
  spHasDuplicateFiles?: boolean;
  setSelectedCheckinType?: (checkinType: CheckinTypes) => void;
  onCancel?: (selectedFileOption: FileOption) => void;
  saveMode: SaveMode;
  setSaveMode: (saveMode: SaveMode) => void;
}

export const CMMultipleFilesMetaDataPanel = ({ closePanel, panelIsOpen, originalInputValues, fileOptions, setFileOptions, saveFiles, checkForDuplicates, setCheckForDuplicates, showSelectVersion, spHasDuplicateFiles, setSelectedCheckinType, onCancel, saveMode, setSaveMode }: ICMMultipleFilesMetaDataPanelProps) => {
  const languageStrings = React.useContext(LanguageContext);

  const dispatch = useDispatch();

  const files = useSelector((state: RootState) => !DragAndDropHelper.isDroppedModeActive ? state.files.files.filter(x => x.isSelected === true) : state.files.files.filter(x => x.isSelected === true && x.isFileSystemEntry && !x.isFolder));
  const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);
  const siteInfo = useSelector((state: RootState) => state.tenantInfo?.sites);
  const tenantReplaceFileNameWithGUID = useSelector((state: RootState) => state.tenantInfo?.replaceFileNameWithGUID);
  const isBusySaving = useSelector((state: RootState) => state.savingInBackground.isBusySaving);
  const canOverwrite = useSelector((state: RootState) => state.files.canOverwrite);
  const datasourceValues = useSelector((state: RootState) => state.datasource.values);
  const datasourceInitialLoad = useSelector((state: RootState) => state.datasource.initialLoad);
  const datasourceCallerId = useSelector((state: RootState) => state.datasource?.callerId);
  const datasourceItems = useSelector((state: RootState) => state.tenantInfo.datasources);
  const convertEmailToPDF = useSelector((state: RootState) => state.user.settings.convertEmailToPDF);
  const savingLocation = useSelector((state: RootState) => state.savingLocation);

  const initialLoad = React.useRef(true);

  const [selectedFile, _setSelectedFile] = React.useState<FileOption>();
  const selectedFileRef = React.useRef<FileOption>();
  const setSelectedFile = (fileOption: FileOption) => {
    selectedFileRef.current = fileOption;
    _setSelectedFile(fileOption);
  };

  const [inputValues, setInputValues] = React.useState<SharePointInputData[]>([]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [savingButtonProps, setSavingButtonProps] = React.useState<ISaveButtonProps>();
  const [saveAllFilesDisabledTooltip, setSaveAllFilesDisabledTooltip] = React.useState("");
  const [saveFileDisabledTooltip, setSaveFileDisabledTooltip] = React.useState("");
  const [updatedName, setUpdatedName] = React.useState("");
  const [verifyingFiles, setVerifyingFiles] = React.useState(false);
  const [fieldsLoaded, setFieldsLoaded] = React.useState(false);
  const [selectionOpen, setSelectionOpen] = React.useState(false);

  React.useEffect(() => {
    if (checkForDuplicates || (initialLoad.current && selectedFileRef.current)) {
      // checkFileNamesForDuplicates(files);
      checkFileForDuplicates();
      initialLoad.current = false;
    }
  }, [checkForDuplicates, initialLoad.current, selectedFileRef.current])

  React.useEffect(() => {
    if (showSelectVersion || spHasDuplicateFiles) {
      setSelectionOpen(true);
    } else {
      setSelectionOpen(false);
    }
  }, [showSelectVersion, spHasDuplicateFiles])

  React.useEffect(() => {
    generalModule.setIsMultiMetaFileUpdatePanelOpen(true);
    setSelectedCheckinType(CheckinTypes.minorCheckIn);
  }, []);

  React.useEffect(() => {
    if (datasourceValues) {
      updateMetadataPropertiesFromDataSource(datasourceValues);
    }
  }, [datasourceValues, selectedFile, fileOptions]);

  React.useEffect(() => {
    if (updatedName) {
      setVerifyingFiles(true);
    }

    let debounceTimer = setTimeout(() => {
      if (updatedName) {
        checkFileForDuplicates(updatedName);
      } else {
        setVerifyingFiles(false);
      }
    }, 800);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [updatedName]);

  React.useEffect(() => {
    if (selectedFile) {
      const selectedFileOption = fileOptions?.find(x => x.key === selectedFile.key);
      if (selectedFileOption) {
        const selectedFileHasChanged = JSON.stringify(selectedFileOption) !== JSON.stringify(selectedFile);
        if (selectedFileHasChanged) {
          setSelectedFile(selectedFileOption);
        }
      }
    }
  }, [selectedFile, fileOptions]);

  React.useEffect(() => {
    setSavingButtonProps(prevVal => {
      return {
        ...prevVal,
        text: saveMode === SaveMode.Default
          ? `${languageStrings.SaveTo} ${savingLocation.display}`
          : `${languageStrings.SaveTo} ${savingLocation.display} ${languageStrings.AsPdf}`
      };
    });
  }, [saveMode, savingLocation]);

  const canConvertToPDF = React.useMemo(() => {
    return files.filter(f => f.isSelected).some(f => f.canSaveAsPDF);
  }, [savingButtonProps, files]);

  const panelCssStyles = React.useMemo(() => {
    const partialStyles: Partial<IPanelStyles> = {
      main: {
        overflow: "hidden"
      },
      content: {
        height: canConvertToPDF ? "calc(100vh - 190px);" : "calc(100vh - 140px);",
        paddingLeft: 5,
        paddingRight: 5,
        paddingBottom: 0
      },
      footer: {
        backgroundColor: "#fff"
      },
    };
    return partialStyles;
  }, [canConvertToPDF]);

  const updateMetadataPropertiesFromDataSource = (values: any) => {
    if (selectedFile && fileOptions?.length > 0) {
      const isMultipleValues: boolean = Array.isArray(values) && values.length > 0;
      const propertyKeys = isMultipleValues
        ? Object.keys(values[0])
        : Object.keys(values);

      if (propertyKeys.length > 0) {
        const selectedFileInputValues = fileOptions.find(x => x.key === selectedFile.key)?.fileInputValues;
        if (selectedFileInputValues) {
          const updatedinputValuesToSave = [...selectedFileInputValues];
          propertyKeys.forEach((propKey) => {
            let updatedValue: any;
            if (isMultipleValues) {
              const uniqueValues = values
                .map(value => value[propKey])
                .filter((value, index, self) => self.indexOf(value) === index);
              updatedValue = uniqueValues;
            }
            else {
              updatedValue = values[propKey];
            }
            updateDatasourceDisplayNameInputData(updatedinputValuesToSave, propKey, updatedValue, isMultipleValues);
          });
          updateSharepointMetadataToSave(updatedinputValuesToSave, null);
        }
      }
    }
    dispatch(setDatasourceValues(null));
  };

  const updateDatasourceDisplayNameInputData = (
    updatedinputValuesToSave: SharePointInputData[],
    propKey: string,
    value: any,
    isMultipleValues: boolean
  ) => {
    const selectedFileInputValues = fileOptions.find(x => x.key === selectedFile.key)?.fileInputValues;

    let inputDatasourceId: number;
    let inputToUpdate = selectedFileInputValues.find(
      (item) => {
        // item.displayName && item.displayName.trim().toLowerCase() === propKey.trim().toLowerCase()
        const boundName: string = item.displayName?.trim()?.toLowerCase();
        if (boundName && item.internalName !== sharePointInternalFileName && boundName === propKey.trim().toLowerCase()) {
          inputDatasourceId = datasourceCallerId && DatasourceHelper.getInputDataSource(datasourceItems, boundName, [currentDirectory?.siteDisplayName ?? ""], [currentDirectory?.DocumentLibrary ?? ""])?.id;
          return true;
        }
        return false;
      }
    );

    if (inputToUpdate) {
      const inputDatasourceCompatible: boolean = Number.isInteger(inputDatasourceId) && inputDatasourceId === datasourceCallerId;
      if (value === refrehOptionsRequest) {
        const updatedInput: SharePointInputData = inputDatasourceCompatible && updatedinputValuesToSave[inputToUpdate.index];
        if (updatedInput) {
          updatedInput.resetDatasourceOptionsToggle = !updatedInput.resetDatasourceOptionsToggle;
        }
      }
      else if (isMultipleValues || inputDatasourceCompatible) { //value param is an array
        try {
          const updatedInput: SharePointInputData = inputDatasourceCompatible && updatedinputValuesToSave[inputToUpdate.index];
          if (updatedInput) {
            if (!Array.isArray(value)) {
              value = [value];
            }
            if (updatedInput.value) {
              if (Array.isArray(updatedInput.value) && updatedInput.value.length > 0) {
                updatedInput.value = updatedInput.value
                  .map(val => {
                    if (val && value.some(x => x?.trim().toLowerCase() === val.trim().toLowerCase())) {
                      return val;
                    }
                    return null;
                  })
                  .filter(val => val);
              }
              else if (!datasourceInitialLoad) {
                if (value.length === 1) {
                  DatasourceValidationHelper.setSingleDatasourceResultValue(updatedinputValuesToSave, inputToUpdate, value[0]);
                }
                else if ((value.length > 1 && !value.some(x => x?.trim().toLowerCase() === updatedInput?.value?.trim().toLowerCase()))) {
                  updatedInput.value = null;
                }
              }
            }
            else if (value.length === 1) {
              DatasourceValidationHelper.setSingleDatasourceResultValue(updatedinputValuesToSave, inputToUpdate, value[0]);
            }
            updatedInput.updatedDatasourceOptions = value;
          }
        } catch (error) {
          logger(error, "CMMultipleFilesMetaDataPanel.tsx (updateDatasourceDisplayNameInputData)");
        }
      }
      else {
        DatasourceValidationHelper.setSingleDatasourceResultValue(updatedinputValuesToSave, inputToUpdate, value);
      }
    }
  };

  const fieldsChanged = async (fields: any[], contentTypeId: string, inputValues?: SharePointInputData[]) => {
    if (fileOptions?.length === 0) {
      // Await the population, not just the cache: opening the per-file properties
      // can race the header load in OutlookService.getItems.
      const selectedItems = await getSelectedExchangeItemsAsync();

      const options = await Promise.all((files as IFileToUpload[])?.map(async (file, index) => {
        // Resolve purely by id: only email files (and their attachments) carry a
        // graphMailItemId, in both the content-manager copy-emails flow and the
        // Outlook multi-select flow, so non-email files always resolve to null.
        const selectedItem = selectedItems?.find(x => x.id === file.graphMailItemId) ?? null;

        return {
          key: file.id,
          text: file.name,
          file: file,
          isSelectedFile: index === 0,
          isModified: false,
          canOverwrite,
          isSaving: false,
          savingSuccess: false,
          savingError: false,
          fileInputValues: await Promise.all(inputValues.map(async inputValue => {
            let extention;
            let value = inputValue.value;
            if (inputValue.internalName === sharePointInternalFileName || inputValue.internalName === sharePointInternalTitleName) {
              value = file.name;
              if (inputValue.internalName === sharePointInternalFileName) {
                extention = file.extension;
              }
            } else {
              value = originalInputValues.find(x => x.internalName === inputValue.internalName)?.value;
            }

            value = resolveEmailMappedInputValue(inputValue, value, selectedItem);

            let hasError = inputValue.hasError;
            if (inputValue.internalName === sharePointInternalFileName) {
              hasError = false;
            }
            else if (inputValue.xml?.Required === "TRUE" && value) {
              hasError = false;
            }

            return {
              ...inputValue,
              hasError,
              value,
              extention
            }
          })),
        }
      }));

      setFileOptions(options);
      setSelectedFile((options as any[])[0]);
      setFieldsLoaded(true);
    }
    else {
      const selectedOption = fileOptions.find(x => x.isSelectedFile);
      setSelectedFile(selectedOption ? selectedOption : (fileOptions as any[])[0]);
      setFieldsLoaded(true);
    }
  }

  const onPanelCLose = () => {
    generalModule.setIsMultiMetaFileUpdatePanelOpen(false);
    closePanel();
  }

  const updateSharepointMetadataToSave = (metadata: SharePointInputData[], informationProtectionId: string, changedIndex?: number) => {
    try {
      if (changedIndex !== undefined) {
        const changedInput = metadata.find(x => x.index === changedIndex);
        if (changedInput?.internalName === sharePointInternalFileName) {
          setUpdatedName(changedInput.value);
        }
      }

      const newFileOptions = fileOptions.map(option => {
        if (selectedFile && selectedFile?.key === option.key) {
          return {
            ...option,
            isModified: true,
            fileInputValues: metadata.map(inputValue => {
              let value = inputValue.value;

              return {
                ...inputValue,
                value
              }
            })
          }
        }

        return {
          ...option
        }
      });

      setFileOptions(newFileOptions);
    } catch (error) {
      logger(error, "CMMultipleFilesMetaDataPanel.tsx (updateSharepointMetadataToSave)");
    }
  };

  const checkFileForDuplicates = async (updatedName?: string) => {
    if (selectedFileRef.current?.file) {
      try {
        const file: IFileToUpload = { ...selectedFileRef.current.file, name: updatedName ? updatedName : selectedFileRef.current.file.name };

        let preCommitResult: IFilePreCommitResult;
        if (!file.isFolder && !file.parentFolderId) {

          const dest = await resolveDestinationUrl(currentDirectory);

          preCommitResult = await getFilePreCommitInfo({
            accessToken: await authModule.getSharePointAccessToken(),
            destinationUrl: dest,
            siteUrl: currentDirectory.webUrl,
            fileName: (file.isMainMailItem && convertEmailToPDF) || (saveMode === SaveMode.SaveAsPdf && file.canSaveAsPDF) ? `${file.name}.pdf` : `${file.name}.${file.extension}`,
            autoFolderCreation: !(new FolderView(siteInfo, currentDirectory).canViewFolder())
          });
        }

        if (!preCommitResult) {
          preCommitResult = { exists: false, checkedOut: false, checkedOutBy: null, locked: false, lockedBy: null };
        }

        const fileToSet: IFileToUpload = {
          ...file,
          isDuplicate: preCommitResult.exists,
          filePreCommitResult: preCommitResult
        }

        dispatch(setFile(fileToSet));

        const updatedFileOptions = fileOptions.map(option => {
          if (selectedFileRef.current.key === option.key) {
            return {
              ...option,
              fileInputValues: option.fileInputValues.map(inputValue => {
                return {
                  ...inputValue,
                  hasError: (inputValue.internalName === sharePointInternalFileName && preCommitResult.exists) ? true : inputValue.hasError,
                  errorMessage: (inputValue.internalName === sharePointInternalFileName && preCommitResult.exists) ? languageStrings.FileSameName : inputValue.errorMessage,
                }
              }),
              file: fileToSet,
              name: fileToSet.name,
              text: fileToSet.name
            }
          }

          return {
            ...option
          }
        });

        setSelectedFile(updatedFileOptions.find(x => x.key === selectedFileRef.current.key));
        setFileOptions(updatedFileOptions);
      }
      catch (error) {
        logger(error, "CMMultipleFilesMetaDataPanel.tsx (checkFileNamesForDuplicates)");
      }
    }
    setCheckForDuplicates(false);
    setVerifyingFiles(false);
  }

  const onFileChange = (event: React.FormEvent<HTMLDivElement>, selectedOption?: FileOption, index?: number) => {
    const newFileOptions = fileOptions.map(option => {
      return {
        ...option,
        isSelectedFile: selectedOption.key === option.key ? true : false,
      }
    })
    setFileOptions(newFileOptions);

    const option = newFileOptions.find(x => x.key === selectedOption.key);
    setSelectedFile(option);
    setUpdatedName(option.file.name);
  }

  const setSelectedIndividualFile = (selectedIndividualFile: FileOption) => {
    setSelectedFile(selectedIndividualFile);

    setFileOptions(fileOptions.map(fileOption => {
      if (fileOption.key === selectedIndividualFile.key) {
        return {
          ...selectedIndividualFile,
          isModified: true
        }
      }

      return { ...fileOption }
    }));
  }

  const isSaveAllFilesButtonDisabled = React.useMemo(() => {
    if (verifyingFiles || isBusySaving || selectedFile?.isSaving) {
      return true;
    }

    if (currentSharepointFolder.userPermissions?.canEditListItem === false) {
      setSaveAllFilesDisabledTooltip(languageStrings.WrongUserPermissionsUpdate);
      return true;
    }

    if (selectedFile?.file?.isDuplicate && !selectedFile?.canOverwrite) {
      setSaveAllFilesDisabledTooltip(languageStrings.OverwriteRequired);
      return true;
    }

    let isDisabled = false;
    fileOptions.forEach(fileOption => {
      if (!isDisabled && fileOption?.file?.isDuplicate && !fileOption?.canOverwrite) {
        isDisabled = true;
      }
    });

    setSaveAllFilesDisabledTooltip(isDisabled ? languageStrings.OverwriteRequired : "");
    return isDisabled;
  }, [currentSharepointFolder, selectedFile, fileOptions, verifyingFiles, isBusySaving]);

  const isSaveFileButtonDisabled = React.useMemo(() => {
    if (verifyingFiles || isBusySaving) {
      return true;
    }

    if (currentSharepointFolder.userPermissions?.canEditListItem === false) {
      setSaveFileDisabledTooltip(languageStrings.WrongUserPermissionsUpdate);
      return true;
    }

    setSaveFileDisabledTooltip("");
    return false;
  }, [currentSharepointFolder, selectedFile, verifyingFiles, isBusySaving])

  const onRenderOption = (option: FileOption): JSX.Element => {
    return (
      <Stack horizontal verticalAlign="center" tokens={stackTokens} styles={fileOptionStackStyles}>
        <Icon
          {...getFileTypeIconProps({
            extension: option.file.extension,
            size: 20,
            imageFileType: "svg",
          })}
          style={fileTypeIconStyles}
        />
        <TooltipHost content={option.text} styles={fileNameStyles} overflowMode={TooltipOverflowMode.Self}>
          <span>{option.text}</span>
        </TooltipHost>
      </Stack>
    );
  };

  const onRenderTitle = (options: FileOption[]): JSX.Element => {
    const option = options?.length > 0 && options[0];
    return (
      <Stack horizontal verticalAlign="center" tokens={stackTokens}>
        <Icon
          {...getFileTypeIconProps({
            extension: option?.file.extension,
            size: 20,
            imageFileType: "svg",
          })}
          style={fileTypeIconStyles}
        />
        <TooltipHost content={option.text} styles={fileNameStyles} overflowMode={TooltipOverflowMode.Self}>
          <span>{option?.text}</span>
        </TooltipHost>
      </Stack>
    );
  };

  const onRenderFooterContent = React.useCallback(
    () => (
      <Stack>
        {canConvertToPDF
          && <Toggle
            label={languageStrings.SaveAsPDFQuestion}
            checked={saveMode === SaveMode.SaveAsPdf}
            onText={languageStrings.Yes}
            offText={languageStrings.No}
            onChange={(e, checked) => setSaveMode(checked ? SaveMode.SaveAsPdf : SaveMode.Default)}
          />}
        <TooltipHost content={saveAllFilesDisabledTooltip} calloutProps={tooltipHostCalloutProps}>
          <PrimaryButton
            className={!generalModule.isInConentMangerMode() ? (getPlatform == Office.PlatformType.iOS ? "xb-button-save ios-clean-button" : "xb-button-save") : "xb-button-save"}
            disabled={isSaveAllFilesButtonDisabled}
            iconProps={{ iconName: 'SaveAll' }}
            onClick={() => {
              saveFiles(saveMode, undefined, selectionOpen, files.some(x => x.parentFolderId));
              setSelectionOpen(false);
            }}
            text={verifyingFiles ? languageStrings.VerifyingFiles : savingButtonProps?.text}
          >
            {isBusy && <Spinner size={SpinnerSize.small} />}
          </PrimaryButton>
        </TooltipHost>
      </Stack>
    ),
    [saveAllFilesDisabledTooltip, isSaveAllFilesButtonDisabled, verifyingFiles, saveFileDisabledTooltip, isSaveFileButtonDisabled, selectedFile, isBusy, selectionOpen, saveMode, savingButtonProps, files],
  );

  return (
    <Panel
      headerText={languageStrings.UpdateIndividualFile}
      isOpen={panelIsOpen}
      onDismiss={() => onPanelCLose()}
      isLightDismiss={false}
      closeButtonAriaLabel="Close"
      type={generalModule.isInConentMangerMode() ? PanelType.custom : PanelType.smallFluid}
      customWidth={generalModule.isInConentMangerMode() ? '350px' : undefined}
      styles={panelCssStyles}
      onRenderFooterContent={onRenderFooterContent}
      isFooterAtBottom={true}
    >
      {selectionOpen && <div className="xb-content-lite">
        {spHasDuplicateFiles && <DuplicateFiles showSelectVersion={showSelectVersion} onCancel={() => { onCancel(selectedFile); setSelectionOpen(false); }} />}
        {showSelectVersion && <VersionSelectorMinMajOnly checkinTypeChanged={setSelectedCheckinType} onCancel={() => { onCancel(selectedFile); setSelectionOpen(false); }} labelFontSize={16} paddingTop={0} paddingLeft={0} />}
      </div>}
      <Stack tokens={containerStackTokens} styles={containerStackStyles} style={{ display: selectionOpen ? "none" : "" }}>
        <Stack.Item>
          {selectedFile?.isSaving === true &&
            <MessageBar>
              <Stack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: 10 }}>
                <Stack.Item>{`${languageStrings.SavingFiles}...`}</Stack.Item>
                <Stack.Item><Spinner size={SpinnerSize.small} /></Stack.Item>
              </Stack>
            </MessageBar>}
          {selectedFile?.savingSuccess === true && <MessageBar messageBarType={MessageBarType.success}>{languageStrings.FileSaveSuccessSingle}</MessageBar>}
          {selectedFile?.savingError === true && <MessageBar messageBarType={MessageBarType.error}>{languageStrings.ErrorMessageGeneric}</MessageBar>}
        </Stack.Item>
        <Stack.Item>
          <div style={dropdownStyles}>
            <Dropdown
              placeholder={languageStrings.SelectAFile}
              label={languageStrings.SelectedFile}
              options={fileOptions}
              onChange={onFileChange}
              selectedKey={selectedFile?.key}
              // responsiveMode={ResponsiveMode.small}
              onRenderOption={onRenderOption}
              onRenderTitle={onRenderTitle}
            />
            <Separator />
          </div>
        </Stack.Item>
        <Stack.Item grow styles={metadataStackItemStyles}>
          <div className="xb-content">
            <SaveWithMetaData
              isInfoMode={true}
              inputValuesToSave={selectedFile ? selectedFile.fileInputValues : inputValues}
              updateSharepointMetadataToSave={updateSharepointMetadataToSave}
              fieldsChanged={fieldsChanged}
              addNameField={!tenantReplaceFileNameWithGUID}
              selectedIndividualFile={selectedFile}
              setSelectedIndividualFile={setSelectedIndividualFile}
            ></SaveWithMetaData>
          </div>
        </Stack.Item>
      </Stack>
      <ToastContainer autoClose={3000} hideProgressBar transition={Slide}></ToastContainer>
    </Panel>
  )
}
